import { Pencil, Type, Eye, EyeOff, Trash2, Users } from "lucide-react";

const COLORS = [
  { value: "#ff4444", label: "Rouge" },
  { value: "#ff9900", label: "Orange" },
  { value: "#22c55e", label: "Vert" },
  { value: "#3b82f6", label: "Bleu" },
  { value: "#a855f7", label: "Violet" },
  { value: "#ffffff", label: "Blanc" },
];

const THICKNESSES = [1, 2, 4, 6, 8];

const AnnotationToolbar = ({
  activeTool,
  onToolChange,
  color,
  onColorChange,
  thickness,
  onThicknessChange,
  showAnnotations,
  onToggleAnnotations,
  annotations,
  currentUserId,
  visibleUsers,
  onVisibleUsersChange,
  onDeleteLast,
}) => {
  // Build unique user list from annotations
  const users = [];
  const seen = new Set();
  for (const ann of annotations) {
    if (!seen.has(ann.user_id)) {
      seen.add(ann.user_id);
      users.push({ user_id: ann.user_id, username: ann.username });
    }
  }

  const toggleUser = (uid) => {
    if (visibleUsers.includes(uid)) {
      onVisibleUsersChange(visibleUsers.filter((u) => u !== uid));
    } else {
      onVisibleUsersChange([...visibleUsers, uid]);
    }
  };

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 bg-[#111113]/90 border border-white/[0.12] rounded-2xl px-3 py-2 backdrop-blur-xl shadow-2xl">
      {/* Draw tool */}
      <ToolBtn
        active={activeTool === "draw"}
        onClick={() => onToolChange(activeTool === "draw" ? "none" : "draw")}
        title="Stylo (tracé libre)"
      >
        <Pencil size={15} />
      </ToolBtn>

      {/* Text tool */}
      <ToolBtn
        active={activeTool === "text"}
        onClick={() => onToolChange(activeTool === "text" ? "none" : "text")}
        title="Texte positionné"
      >
        <Type size={15} />
      </ToolBtn>

      <Divider />

      {/* Colors */}
      <div className="flex items-center gap-1">
        {COLORS.map((c) => (
          <button
            key={c.value}
            title={c.label}
            onClick={() => onColorChange(c.value)}
            className="rounded-full transition-all flex items-center justify-center"
            style={{
              width: 18,
              height: 18,
              backgroundColor: c.value,
              outline: color === c.value ? `2px solid white` : `2px solid transparent`,
              outlineOffset: 1,
              boxShadow: c.value === "#ffffff" ? "0 0 0 1px rgba(255,255,255,0.2)" : undefined,
            }}
          />
        ))}
      </div>

      <Divider />

      {/* Thickness */}
      <div className="flex items-center gap-1">
        {THICKNESSES.map((t) => (
          <button
            key={t}
            title={`Épaisseur ${t}`}
            onClick={() => onThicknessChange(t)}
            className={`rounded-full transition-all flex items-center justify-center ${thickness === t ? "bg-white/20" : "hover:bg-white/10"}`}
            style={{ width: 22, height: 22 }}
          >
            <div
              className="rounded-full bg-white"
              style={{ width: Math.max(3, t * 1.5), height: Math.max(3, t * 1.5) }}
            />
          </button>
        ))}
      </div>

      <Divider />

      {/* Show/hide annotations */}
      <ToolBtn
        active={showAnnotations}
        onClick={onToggleAnnotations}
        title={showAnnotations ? "Masquer les annotations" : "Afficher les annotations"}
      >
        {showAnnotations ? <Eye size={15} /> : <EyeOff size={15} />}
      </ToolBtn>

      {/* Per-user filter (only if multiple users) */}
      {users.length > 1 && (
        <div className="relative group">
          <ToolBtn active={visibleUsers.length < users.length} title="Filtrer par auteur">
            <Users size={15} />
          </ToolBtn>
          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:flex flex-col gap-1 bg-[#1a1a1e] border border-white/[0.1] rounded-xl p-2 min-w-[140px] shadow-2xl">
            {users.map((u) => (
              <button
                key={u.user_id}
                onClick={() => toggleUser(u.user_id)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] text-left transition-all ${
                  visibleUsers.includes(u.user_id)
                    ? "bg-violet-500/20 text-violet-200"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                <div
                  className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                >
                  {u.username[0].toUpperCase()}
                </div>
                {u.username}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Delete last annotation */}
      {annotations.filter((a) => a.user_id === currentUserId).length > 0 && (
        <ToolBtn onClick={onDeleteLast} title="Supprimer ma dernière annotation" danger>
          <Trash2 size={14} />
        </ToolBtn>
      )}
    </div>
  );
};

const ToolBtn = ({ children, active, onClick, title, danger }) => (
  <button
    onClick={onClick}
    title={title}
    className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${
      danger
        ? "text-red-400 hover:bg-red-500/20"
        : active
        ? "bg-violet-500/30 text-violet-200 border border-violet-500/40"
        : "text-white/50 hover:text-white/80 hover:bg-white/[0.07]"
    }`}
  >
    {children}
  </button>
);

const Divider = () => (
  <div className="w-px h-5 bg-white/[0.1] mx-0.5" />
);

export default AnnotationToolbar;
